import { createFileRoute, Link } from "@tanstack/react-router";
import { IMAGES } from "@/lib/images";
import { HeroBgImage } from "@/components/HeroBgImage";
import { BookOpen, ArrowRight, Sun, Shield, Layers, Sparkles, Clock, BookText } from "lucide-react";

const GUIDES = [
  {
    to: "/guider/solfilm-kontor",
    icon: Sun,
    title: "Solfilm för kontor — guide till energibesparing & komfort",
    summary: "Hur 3M solfilm sänker kylbehov, reducerar bländning på skärmar och betalar sig på 3–5 år. Med tabell över Prestige-seriens prestanda.",
    cat: "Solfilm · B2B",
    time: 9,
  },
  {
    to: "/guider/solfilm-villa",
    icon: Sun,
    title: "Solfilm för villa & lägenhet — så slipper du värme och bländning hemma",
    summary: "Hur solfilm hjälper mot varma sovrum, blekta möbler och tv-bländning. UV-skydd, energibesparing och vilka rum som tjänar mest.",
    cat: "Solfilm · Privat",
    time: 8,
  },
  {
    to: "/guider/sakerhetsfilm-inbrottsskydd",
    icon: Shield,
    title: "Säkerhetsfilm för inbrott och splitterskydd — EN 12600 och AFS 2020:1",
    summary: "Hur 3M Ultra säkerhetsfilm fördröjer inbrott och håller glaset på plats vid kross. Klassningar, regelverk och var den krävs.",
    cat: "Säkerhet",
    time: 10,
  },
  {
    to: "/guider/dekorfilm-frostat-glas",
    icon: Layers,
    title: "Dekorfilm & frostat glas — insynsskydd och branding på fönster",
    summary: "När frostfilm slår persienner: mötesrum, glasväggar, butiksskyltar, logotyper. Tekniker, livslängd och designval.",
    cat: "Dekor",
    time: 7,
  },
  {
    to: "/guider/solfilm-vs-persienner",
    icon: Sparkles,
    title: "Solfilm eller persienner? Komplett jämförelse 2026",
    summary: "Sex parametrar — värmereduktion, ljus, drift, underhåll, livslängd, kostnad. Och varför många kontor använder båda.",
    cat: "Jämförelse",
    time: 8,
  },
  {
    to: "/guider/underhall-livslangd",
    icon: Clock,
    title: "Skötsel & livslängd för fönsterfilm — rengöring, garanti och åldrande",
    summary: "Hur du rengör solfilm utan att skada den, vilka medel som förkortar livslängden och vad 3M:s 10-årsgaranti faktiskt täcker.",
    cat: "Underhåll",
    time: 6,
  },
  {
    to: "/ordlista",
    icon: BookText,
    title: "Ordlista — fönsterfilm-termer förklarade",
    summary: "TSER, LT, VLT, UV-blockering, EN 12600, AFS 2020:1, splitterskydd, low-E och 20+ andra termer förklarade på svenska.",
    cat: "Referens",
    time: 5,
  },
] as const;

export const Route = createFileRoute("/guider/")({
  head: () => ({
    meta: [
      { title: "Guider — fönsterfilm, solfilm & säkerhetsfilm | BBN Nordic" },
      { name: "description", content: "Djupgående guider om solfilm, säkerhetsfilm och dekorfilm: energibesparing, inbrottsskydd, dekor, jämförelser och skötsel. Skrivet av BBN Nordic — auktoriserad 3M-installatör." },
      { property: "og:title", content: "Guider om fönsterfilm — BBN Nordic" },
      { property: "og:description", content: "7 djupguider om solfilm, säkerhetsfilm, dekorfilm + ordlista." },
      { property: "og:url", content: "https://bbnnordic.se/guider" },
      { property: "og:image", content: IMAGES.solfilmHero },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/guider" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Guider om fönsterfilm",
        url: "/guider",
        description: "BBN Nordics guider om solfilm, säkerhetsfilm och dekorfilm.",
        hasPart: GUIDES.map((g) => ({
          "@type": "Article",
          name: g.title,
          url: g.to,
        })),
      }),
    }],
  }),
  component: GuiderIndex,
});

function GuiderIndex() {
  return (
    <main className="bg-background">
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-16 bg-navy text-cream overflow-hidden">
        <HeroBgImage src={IMAGES.installer} alt="BBN Nordic — kunskapsbank och guider om fönsterfilm" />
        <div className="relative mx-auto max-w-4xl px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold-bright font-semibold mb-4">
            <BookOpen size={14} /> Kunskapsbank
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight">
            Guider om fönsterfilm
          </h1>
          <p className="mt-6 text-lg md:text-xl text-cream/85 max-w-2xl font-light leading-relaxed">
            Allt vi vet om solfilm, säkerhetsfilm och dekorfilm — sammanställt så att
            du kan fatta beslut utan att gissa. Faktagranskat mot 3M:s och
            Solar Screens datablad samt gällande EN- och AFS-standarder.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/offert"
              className="btn-glow inline-flex items-center justify-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold min-h-11"
            >
              Få gratis offert <ArrowRight size={16} />
            </Link>
            <a
              href="tel:+46771502502"
              className="inline-flex items-center justify-center gap-2 text-cream/85 px-4 py-3 font-medium hover:text-gold-bright transition-colors min-h-11"
            >
              Ring 0771 502 502
            </a>
          </div>
        </div>
      </section>




      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 md:px-10 grid gap-5 md:grid-cols-2">
          {GUIDES.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              className="group glass rounded-2xl p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">
                  <g.icon size={14} /> {g.cat}
                </span>
                <span className="text-xs text-muted-foreground">{g.time} min</span>
              </div>
              <h2 className="font-display text-xl md:text-2xl text-foreground mb-3 leading-tight">
                {g.title}
              </h2>
              <p className="text-foreground/75 text-sm leading-relaxed flex-1">{g.summary}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-bbn-primary group-hover:text-bbn-accent transition-colors">
                Läs guiden <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
